import { useState, useCallback } from 'react';

export interface SortRule {
  fieldId: string;
  direction: 'asc' | 'desc';
}

export interface MultiSortState {
  fullMatchesFirst: boolean;
  sorts: SortRule[];
}

const DEFAULT_STATE: MultiSortState = {
  fullMatchesFirst: false,
  sorts: []
};

export const useMultiSort = (initialState: MultiSortState = DEFAULT_STATE) => {
  const [state, setState] = useState<MultiSortState>(initialState);

  const clear = useCallback(() => {
    setState({ fullMatchesFirst: false, sorts: [] });
  }, []);

  const updateState = useCallback((updates: Partial<MultiSortState>) => {
    setState(prev => ({ ...prev, ...updates }));
  }, []);

  const addSort = useCallback((fieldId: string = '', direction: 'asc' | 'desc' = 'asc') => {
    setState(prev => ({
      ...prev,
      sorts: [...prev.sorts, { fieldId, direction }]
    }));
  }, []);

  const removeSort = useCallback((index: number) => {
    setState(prev => ({
      ...prev,
      sorts: prev.sorts.filter((_, i) => i !== index)
    }));
  }, []);

  const updateSort = useCallback((index: number, updates: Partial<SortRule>) => {
    setState(prev => ({
      ...prev,
      sorts: prev.sorts.map((sort, i) =>
        i === index ? { ...sort, ...updates } : sort
      )
    }));
  }, []);

  const reorderSorts = useCallback((fromIndex: number, toIndex: number) => {
    setState(prev => {
      const newSorts = [...prev.sorts];
      const [moved] = newSorts.splice(fromIndex, 1);
      newSorts.splice(toIndex, 0, moved);
      return { ...prev, sorts: newSorts };
    });
  }, []);

  return {
    state,
    clear,
    updateState,
    addSort,
    removeSort,
    updateSort,
    reorderSorts
  };
};
